/**
 * Tasks User Settings Form
 * User preferences for tasks module
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { Loader2 } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Switch } from '../../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { useNotification } from '../../context/NotificationContext';
import { useApiError } from '../../hooks/useApiError';
import { useWorkManagementUserSettings, useUpdateWorkManagementUserSettings } from '../../admin/hooks/useWorkManagementSettings';
import { EntityLoadState } from '../common';
import { ExportImportSection } from './ExportImportSection';

interface FormData {
  default_view: string;
  show_completed_tasks: boolean;
  compact_mode: boolean;
}

const VIEW_OPTIONS = [
  { value: 'table', labelKey: 'tasks:settings.userSettings.views.table' },
  { value: 'kanban', labelKey: 'tasks:settings.userSettings.views.kanban' },
  { value: 'calendar', labelKey: 'tasks:settings.userSettings.views.calendar' },
  { value: 'timeline', labelKey: 'tasks:settings.userSettings.views.timeline' },
];

interface WorkManagementUserSettingsFormProps {
  onSuccess?: () => void;
}

export function WorkManagementUserSettingsForm({ onSuccess }: WorkManagementUserSettingsFormProps) {
  const { t } = useTranslation(['tasks', 'common']);
  const { showSuccess } = useNotification();
  const { handleMutationError } = useApiError();

  const { data: settings, isLoading, error } = useWorkManagementUserSettings();
  const updateMutation = useUpdateWorkManagementUserSettings();

  const { control, handleSubmit, reset, formState: { isDirty } } = useForm<FormData>({
    defaultValues: {
      default_view: 'table',
      show_completed_tasks: true,
      compact_mode: false,
    },
  });

  // Reset form when settings load
  useEffect(() => {
    if (settings) {
      reset({
        default_view: settings.default_view ?? 'table',
        show_completed_tasks: settings.show_completed_tasks ?? true,
        compact_mode: settings.compact_mode ?? false,
      });
    }
  }, [settings, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      await updateMutation.mutateAsync(data);
      showSuccess(t('tasks:settings.saveSuccess'));
      onSuccess?.();
    } catch (error) {
      handleMutationError(error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <EntityLoadState error={error} className="mb-4" />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t('tasks:settings.userSettings.defaultView')}</Label>
          <Controller
            name="default_view"
            control={control}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VIEW_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.value.charAt(0).toUpperCase() + option.value.slice(1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>

        <Controller
          name="show_completed_tasks"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-3">
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                id="show_completed_tasks"
              />
              <Label htmlFor="show_completed_tasks" className="cursor-pointer">
                {t('tasks:settings.userSettings.showCompletedTasks')}
              </Label>
            </div>
          )}
        />

        <Controller
          name="compact_mode"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-3">
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
                id="compact_mode"
              />
              <Label htmlFor="compact_mode" className="cursor-pointer">
                {t('tasks:settings.userSettings.compactMode')}
              </Label>
            </div>
          )}
        />

        <div className="flex justify-end gap-2 pt-4">
          <Button
            type="submit"
            disabled={!isDirty || updateMutation.isPending}
          >
            {updateMutation.isPending && <Loader2 className="h-4 w-4 me-2 animate-spin" />}
            {t('common:app.save')}
          </Button>
        </div>

        {/* Export/Import Section */}
        <ExportImportSection onNavigateAway={onSuccess} />
      </div>
    </form>
  );
}
